import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useUpdateContractFiscalDetails } from '@/features/privateCompanies/hooks/contract/useUpdateContractFiscalDetails'
import {
  UpdateContractFiscalDetailsSchema,
  updateContractFiscalDetailsSchema,
} from '@/features/privateCompanies/schemas/contract/updateFiscalDetails.schema'
import { CompanyContractType } from '@/features/privateCompanies/types/companyContract.type'
import { options as fiscalDocumentConsumerOptions } from '@/features/privateCompanyLeadsFunnel/constants/fiscalDocumentConsumer.constant'
import { options as fiscalDocumentRequirementOptions } from '@/features/privateCompanyLeadsFunnel/constants/fiscalDocumentRequirement.constant'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

interface UpdateFiscalDetailsModalProps {
  contract?: CompanyContractType
}

export const UpdateFiscalDetailsModal = ({ contract }: UpdateFiscalDetailsModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<UpdateContractFiscalDetailsSchema>({
    resolver: zodResolver(updateContractFiscalDetailsSchema),
  })

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['clientContract', contract?.id] })
    setIsOpen(false)
  }

  const { mutate: updateContractFiscalDetails, isPending: updateContractFiscalDetailsIsPending } =
    useUpdateContractFiscalDetails({
      contractId: contract?.id,
      onSuccess,
      onError: FormTransformers.errorsTransformer<UpdateContractFiscalDetailsSchema>({ setError, setMessages }),
    })

  const onSubmit: SubmitHandler<UpdateContractFiscalDetailsSchema> = (data) => {
    updateContractFiscalDetails(data)
  }

  useEffect(() => {
    if (isOpen) {
      reset({
        contract: {
          fiscal_document_requirement: fiscalDocumentRequirementOptions.find(
            ({ value }) => value === contract?.fiscal_document_requirement,
          ),
          fiscal_document_consumer: fiscalDocumentConsumerOptions.find(
            ({ value }) => value === contract?.fiscal_document_consumer,
          ),
        },
      })
    }
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_fiscal_details_modal"
          operationKlassToCheckPermission={'Admin::Client::Contracts::UpdateFiscalDataInformation'}
          className="h-[2.125rem] text-sm text-white-800"
        >
          Editar
        </Button>
      }
      title="Editando detalhes fiscais"
      dataTestid="update_fiscal_details"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700"
      disabled={updateContractFiscalDetailsIsPending}
      confirmButtonIsLoading={updateContractFiscalDetailsIsPending}
      body={
        <>
          <form className="flex flex-col gap-y-4 text-white-800">
            <SelectController
              control={control}
              name="contract.fiscal_document_requirement"
              label="Tipo de recolha"
              errorMessage={errors?.contract?.fiscal_document_requirement?.message}
              options={fiscalDocumentRequirementOptions}
              isDisabled={updateContractFiscalDetailsIsPending}
              markAsRequired
            />

            <SelectController
              control={control}
              name="contract.fiscal_document_consumer"
              label="CNPJ/CPF p/ coleta de nota fiscal (nota emitida pelo credenciado)"
              errorMessage={errors?.contract?.fiscal_document_consumer?.message}
              defaultValue={fiscalDocumentConsumerOptions.find(
                ({ value }) => value === contract?.fiscal_document_consumer,
              )}
              options={fiscalDocumentConsumerOptions}
              isDisabled={updateContractFiscalDetailsIsPending}
              markAsRequired
            />
          </form>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </>
      }
    />
  )
}
